import {type RouteLocation, useRoute, useRouter} from 'vue-router'
import {useToastNotify} from '@/composables/utils/useToastNotify.ts'
import {onMounted} from 'vue'
import {unregisterServiceWorker} from '@/config/setup/registerServiceWorker.ts'

// This composable handles chunk preload errors using Vite and Service Worker. It's logic ensures that
// the app can recover gracefully when a user tries to load a chunk that has been changed by a deployment.

// To do that, we use two mechanisms in parallel: Service Worker and Vite's special event "vite:preloadError".
// Handling the event "vite:preloadError" is a fallback for situations where the Service Worker is not available,
// has not yet updated or is in the process of updating its cache.

// Read more:
// - Service Worker (workbox): https://developer.chrome.com/docs/workbox
// - Vite's special event "vite:preloadError": https://vite.dev/guide/build.html#load-error-handling

// Reload loop guard: track reload attempts in sessionStorage to prevent infinite reload cycles.
// If we've reloaded MAX_RELOAD_ATTEMPTS times within RELOAD_WINDOW_MS, stop trying.
const RELOAD_STORAGE_KEY = 'chunk-reload-attempts'
const MAX_RELOAD_ATTEMPTS = 3
const RELOAD_WINDOW_MS = 30000

const debugEnabled = import.meta.env.VITE_LIVE_UPDATE_DEBUGGER_ENABLED === 'true'

function hasExceededReloadAttempts(): boolean {
  try {
    const raw = sessionStorage.getItem(RELOAD_STORAGE_KEY)
    if (!raw) return false
    const { count, timestamp } = JSON.parse(raw)
    if (Date.now() - timestamp > RELOAD_WINDOW_MS) return false
    return count >= MAX_RELOAD_ATTEMPTS
  } catch {
    return false
  }
}

function trackReloadAttempt(): void {
  try {
    const raw = sessionStorage.getItem(RELOAD_STORAGE_KEY)
    const existing = raw ? JSON.parse(raw) : null
    const withinWindow = existing && Date.now() - existing.timestamp <= RELOAD_WINDOW_MS
    sessionStorage.setItem(RELOAD_STORAGE_KEY, JSON.stringify({
      count: withinWindow ? existing.count + 1 : 1,
      timestamp: withinWindow ? existing.timestamp : Date.now(),
    }))
  } catch {
    // sessionStorage unavailable
    console.warn('SessionStorage unavailable')
  }
}

async function getWaitingSW(): Promise<ServiceWorker | null> {
  if (!('serviceWorker' in navigator)) return null
  const registration = await navigator.serviceWorker?.getRegistration()
  if (!registration?.waiting) return null
  return registration.waiting
}

async function clearCachesUnregisterSW(): Promise<void> {
  // clear ALL caches
  if (typeof caches !== 'undefined' && caches) {
    try {
      const keys = await caches.keys()
      await Promise.all(keys.map(key => caches.delete(key)))
    } catch {
      // Silently continue
      console.warn('Caches not found, failed to clear caches')
    }
  }

  // Unregister Service Worker before reloading to ensure clean state (fire & forget)
  unregisterServiceWorker()
}

// `to.path` is router-normalized, but a leading '//', '/\' or an embedded tab/newline can still
// make the browser resolve the target against a foreign origin. Let the URL parser decide.
// Exported for isolated unit testing — jsdom's `window.location` cannot be mocked to observe
// reload()'s branching in this project's test environment.
export const isSameOriginPath = (path: string): boolean => {
  try {
    return new URL(path, globalThis.location.origin).origin === globalThis.location.origin
  } catch {
    return false
  }
}

const reload = (targetPath?: string) => {
  trackReloadAttempt()
  if (targetPath && isSameOriginPath(targetPath)) {
    globalThis.location.href = targetPath
    return
  }
  globalThis.location.reload()
}

const activateWaitingSW = async (waitingSW: ServiceWorker, toastNotify: ReturnType<typeof useToastNotify>['toastNotify'], targetPath?: string, skipToast?: boolean) => {
  if (!skipToast) await toastNotify({ group: 'swLoader' })

  // Timeout because of chromium bug: https://bugs.chromium.org/p/chromium/issues/detail?id=1175696
  setTimeout(() => {
    waitingSW.postMessage({ type: 'SKIP_WAITING' })

    // Calling SKIP_WAITING on the waiting service worker SHOULD trigger a page reload by itself.
    // However, in some cases it just activates the new SW and does nothing afterward. To overcome this,
    // we take that in our own hands.
    setTimeout(() => {
      reload(targetPath)
    }, 250)
  }, 500)
}

const activateNewDeployment = async (toastNotify: ReturnType<typeof useToastNotify>['toastNotify'], targetPath?: string) => {
  if (hasExceededReloadAttempts()) {
    await clearCachesUnregisterSW()
    reload(targetPath)
    return
  }

  // Tier 1: A waiting SW is already available — activate it immediately, reload the page
  const waitingSW = await getWaitingSW()
  if (waitingSW?.postMessage) {
    await activateWaitingSW(waitingSW, toastNotify, targetPath)
    return
  }

  // Tier 2: No waiting SW yet — attempt a quick update check and wait for install to complete if update exists.
  // This covers the case where the app was dormant and the periodic update check hasn't run yet.
  const INSTALL_TIMEOUT_MS = 60000
  const registration = await navigator.serviceWorker?.getRegistration()
  if (registration) {
    try {
      // check for update
      await registration.update()

      // Quick path: SW already waiting after update (rather unusual to be this quick but check anyway)
      if (registration.waiting?.postMessage) {
        await activateWaitingSW(registration.waiting, toastNotify, targetPath)
        return
      }

      // SW is still installing (downloading imports / precaching) — wait for install to complete
      const installingSW = registration.installing
      if (installingSW) {
        // Show toast directly, since the user is already in error state. It will automatically disappear
        // when page reloads.

        await toastNotify({ group: 'swLoader', duration: INSTALL_TIMEOUT_MS })

        const newlyWaitingSW = await new Promise<ServiceWorker | null>((resolve) => {
          let settled = false
          const settle = (value: ServiceWorker | null) => {
            if (settled) return
            settled = true
            clearTimeout(timeout)
            installingSW.removeEventListener('statechange', onStateChange)
            resolve(value)
          }

          const onStateChange = () => {
            if (installingSW.state === 'installed') settle(registration.waiting)
            if (installingSW.state === 'redundant') settle(null)
          }

          const timeout = setTimeout(() => settle(null), INSTALL_TIMEOUT_MS)
          installingSW.addEventListener('statechange', onStateChange)
          onStateChange()
        })

        // SW is waiting to be activated
        if (newlyWaitingSW?.postMessage) {
          await activateWaitingSW(newlyWaitingSW, toastNotify, targetPath, true)
          return
        }
      }
    } catch {
      // Update check failed or timed out — proceed to tier 3
    }
  }

  // Tier 3: Fallback recovery if nothing worked - clear caches, unregister SW so next page reload inits all again
  await clearCachesUnregisterSW()
  reload(targetPath)
}

// When VITE_LIVE_UPDATE_ENABLED is set, native apps no longer rely on the SW for updates.
// On web we simply reload on chunk errors (AC 7) without any SW activation logic.
function useSimplePreloadErrorHandling() {
  const router = useRouter()
  const chunkErrors = new Set<Error>()
  let isRouteNavigation = false

  window.addEventListener('vite:preloadError', async (event) => {
    if (isRouteNavigation) {
      chunkErrors.add(event.payload)
      return
    }
    event.preventDefault()
    if (hasExceededReloadAttempts()) return
    reload()
  })

  router.beforeEach(() => {
    isRouteNavigation = true
    chunkErrors.clear()
  })

  router.afterEach(() => {
    isRouteNavigation = false
  })

  router.onError(async (error, to) => {
    isRouteNavigation = false
    if (!chunkErrors.has(error)) return
    if (hasExceededReloadAttempts()) return
    debugEnabled && console.log('[LiveUpdate] useChunkPreloadErrorHandling: chunkLoadError detected, route after reload: ', globalThis.location.pathname === to.path ? undefined : to.path)
    reload(globalThis.location.pathname === to.path ? undefined : to.path)
  })

  onMounted(() => {
    try {
      sessionStorage.removeItem(RELOAD_STORAGE_KEY)
    } catch {
      console.warn('SessionStorage unavailable')
    }
  })
}

export function useChunkPreloadErrorHandling() {
  if (import.meta.env.VITE_LIVE_UPDATE_ENABLED === 'true') {
    debugEnabled && console.log('[LiveUpdate] useChunkPreloadErrorHandling: Using simple page reload and only listening to vite:preloadError for chunkLoadErrors')
    useSimplePreloadErrorHandling()
    return
  }

  const { toastNotify } = useToastNotify()
  const router = useRouter()
  const route = useRoute()
  const chunkErrors = new Set<Error>()
  let isRouteNavigation = false

  async function reloadAppAtPath(to: RouteLocation) {
    // No route change, stay on the current path
    if (globalThis.location.pathname === to.path) {
      await activateNewDeployment(toastNotify)
    } else {
      // Route change, reload the app at the new path
      await activateNewDeployment(toastNotify, to.path)
    }
  }

  // Listen to Vite's special event that will be fired if a chunk can't be loaded.
  // For browsers without SW or if SW has not yet updated its cache (happens only every 60s)
  window.addEventListener('vite:preloadError', async (event) => {
    // During route navigation, we let router.onError handle it the preloadError
    if (isRouteNavigation) {
      // Usually we would do event.preventDefault() here, but we want to handle the error in route navigation
      // so we just add the error to the set of chunk errors and let the error propagate.
      chunkErrors.add(event.payload)
      return
    }

    // Outside route navigation - first page load for example. Handle directly.
    event.preventDefault()
    await activateNewDeployment(toastNotify)
  })

  router.beforeEach(() => {
    isRouteNavigation = true
    chunkErrors.clear()
  })

  router.afterEach(() => {
    isRouteNavigation = false
  })

  // Handle chunk preload errors in the router, by activating potentially waiting Service Worker
  // or reloading the app if no SW is available.
  router.onError(async (error, to) => {
    isRouteNavigation = false
    if (!chunkErrors.has(error)) return
    await reloadAppAtPath(to)
  })

  // In addition to router.onError handling, we also check for a waiting Service Worker on every route change,
  // whose route has meta.enableSwReload. This is because we want to activate the new Service Worker as soon as possible
  // and not only when a chunk preload error occurs.
  // Doing that on route change ensures that we don't disrupt the user in their current navigation.
  router.beforeEach(async (to) => {
    const waitingSW = await getWaitingSW()
    if (!to.meta.enableSwReload || !waitingSW || chunkErrors.size > 0) return
    await reloadAppAtPath(to)
  })

  // If for some reason the waiting SW was not installed during the last app usage, make sure to activate it
  // on next app start
  onMounted(async () => {
    // App booted successfully — reset reload loop guard
    try {
      sessionStorage.removeItem(RELOAD_STORAGE_KEY)
    } catch {
      console.warn('SessionStorage unavailable')
    }

    // Monitor does not do routing and is only mounted once. It's also rarely rebooted, so we check regularly
    // for a waiting SW.
    if (window.location.pathname.includes('/monitor/')) {
      setInterval(async () => {
        const waitingSW = await getWaitingSW()
        if (!waitingSW || !route.meta.enableSwHotReload) return
        await reloadAppAtPath(route)
      }, 10 * 1000)
      return
    }

    const waitingSW = await getWaitingSW()
    if (!route.meta.enableSwReload || !waitingSW || chunkErrors.size > 0) return
    await reloadAppAtPath(route)
  })
}

// These errors will be ignored in Sentry reporting, as there are false-positives related to chunk preload errors.
const ignoredErrors = [
  'Importing a module script failed',
  'Unable to preload',
  'Failed to fetch dynamically imported module',
  "'text/html' is not a valid JavaScript MIME type",
  "because its MIME type ('text/html') is not a supported",
]
export function isIgnoredChunkLoadError(errorMessage: string): boolean {
  return ignoredErrors.some(ignoredError => errorMessage.includes(ignoredError))
}
